import { SocketClient } from "cbcore-ts"
import {
    nil,
    NO,
    UIColor,
    UIRectangle,
    UIRoute,
    UITextView,
    UIView,
    UIViewBroadcastEvent,
    UIViewController,
    YES
} from "uicore-ts"
import { RHCore } from "../../Core/RHCore"
import { CBDialogViewShower } from "../../Custom components/CB/CBDialogViewShower"
import { RHButton } from "../../Custom components/RH/RHButton"
import { RHColor } from "../../Custom components/RH/RHColor"
import { RHEuroFormatter } from "../../Custom components/RH/Utils_slop/RHEuroFormatter"
import { RHInvoiceEntry } from "../../SocketClientFunctions"
import { InvoiceLineRowView } from "./InvoiceLineRowView"
import { InvoiceReceiptView } from "./InvoiceReceiptView"


export class InvoicePaymentViewController extends UIViewController {
    
    static override readonly routeComponentName = "invoicePayment"
    static override readonly ParameterIdentifierName = {
        invoiceIds: "invoiceIds"
    }
    
    // ── Confirmation phase ────────────────────────────────────────────────────
    
    readonly confirmationTitleLabel = new UITextView()
        .configuredWithObject({
            text: "Arve tasumine",
            backgroundColor: UIColor.transparentColor,
            style: {
                fontSize: "20px",
                fontWeight: "700",
                lineHeight: "1.3"
            }
        })
        .addedAsSubviewToView(this.view)
    
    readonly confirmationSubtitleLabel = new UITextView()
        .configuredWithObject({
            textColor: RHColor.textSecondary,
            backgroundColor: UIColor.transparentColor,
            style: { fontSize: "13px" }
        })
        .addedAsSubviewToView(this.view)
    
    readonly separatorView = new UIView()
        .configuredWithObject({ backgroundColor: RHColor.border })
        .addedAsSubviewToView(this.view)
    
    private _lineRows: InvoiceLineRowView[] = []
    
    readonly totalSeparatorView = new UIView()
        .configuredWithObject({ backgroundColor: RHColor.border })
        .addedAsSubviewToView(this.view)
    
    readonly totalLabel = new UITextView()
        .configuredWithObject({
            text: "Kokku",
            backgroundColor: UIColor.transparentColor,
            style: { fontSize: "15px", fontWeight: "700" }
        })
        .addedAsSubviewToView(this.view)
    
    readonly totalAmountLabel = new UITextView()
        .configuredWithObject({
            backgroundColor: UIColor.transparentColor,
            textAlignment: UITextView.textAlignment.right,
            style: {
                fontSize: "15px",
                fontWeight: "700",
                fontVariantNumeric: "tabular-nums"
            }
        })
        .addedAsSubviewToView(this.view)
    
    readonly cancelButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Tühista" },
            variant: "softDanger"
        })
        .addedAsSubviewToView(this.view)
    
    readonly confirmButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Kinnita makse" },
            variant: "success"
        })
        .addedAsSubviewToView(this.view)
    
    // ── Receipt phase (swaps in after payment) ────────────────────────────────
    
    readonly receiptView = new InvoiceReceiptView()
    
    readonly receiptCloseButton = new RHButton()
        .configuredWithObject({
            titleLabel: { text: "Sulge" },
            variant: "softDanger"
        })
    
    // ── State ─────────────────────────────────────────────────────────────────
    
    private _invoices: RHInvoiceEntry[] = []
    private _isShowingReceipt = NO
    // The company _invoices was resolved for (GetCompanyInvoices's companyId
    // argument at the time of _loadInvoices). Confirming payment must be
    // guarded against this having drifted from the now-active company -
    // otherwise PayInvoices would be sent a companyId that no longer matches
    // the invoices the user is actually looking at.
    _loadedCompanyId?: string
    // Guards _handleCancel against running its route-removal twice - a
    // company-membership change pushes both a "CompanyMembershipDidChange"
    // and a "CompanyAffiliationDidChange" server message for the same event,
    // each independently triggering activeCompanyDidChange once the client
    // refetches, so viewDidReceiveBroadcastEvent below can legitimately fire
    // this guard twice for what is, from the user's perspective, one event.
    _hasRequestedCancellation = NO

    constructor(view: UIView) {
        super(view)

        this.view.intrinsicContentWidth = () => 520
        
        this.view.configureWithObject({
            backgroundColor: RHColor.surface,
            style: {
                borderRadius: "12px",
                overflowY: "auto"
            }
        })
        
        // noinspection JSConstantReassignment
        this.cancelButton.controlEventTargetAccumulator.PrimaryActionTriggered =
            () => this._handleCancel()
        
        // noinspection JSConstantReassignment
        this.confirmButton.controlEventTargetAccumulator.PrimaryActionTriggered =
            () => this._handleConfirmPayment()
        
        // noinspection JSConstantReassignment
        this.receiptCloseButton.controlEventTargetAccumulator.PrimaryActionTriggered =
            () => this._handleCancel()
    }
    
    override async handleRoute(route: UIRoute) {
        await super.handleRoute(route)
        const component = route.componentWithViewController(InvoicePaymentViewController)
        const invoiceIdsString = component?.parameters[InvoicePaymentViewController.ParameterIdentifierName.invoiceIds] ?? ""
        const invoiceIds = invoiceIdsString ? invoiceIdsString.split(",") : []
        await this._loadInvoices(invoiceIds)
    }
    
    private async _loadInvoices(invoiceIds: string[]) {
        if (invoiceIds.length === 0) {
            return
        }

        // RootViewController reuses one lazily-constructed instance of this
        // controller across every payment dialog opened for the rest of the
        // page's lifetime - reset the cancellation guard here so a later,
        // unrelated payment attempt is not silently unable to cancel because
        // an earlier one already flipped the flag.
        this._hasRequestedCancellation = NO
        const companyId = RHCore.sharedInstance.companyAffiliationStore.activeCompanyId
        this._loadedCompanyId = companyId
        try {
            const [personalInvoices, companyInvoices] = await Promise.all([
                SocketClient.GetMyInvoices().then(r => r.result.invoices).catch(() => [] as RHInvoiceEntry[]),
                SocketClient.GetCompanyInvoices({ companyId })
                    .then(r => r.result.invoices).catch(() => [] as RHInvoiceEntry[])
            ])
            const allInvoices = [...personalInvoices, ...companyInvoices]
            this._invoices = allInvoices.filter(inv => invoiceIds.includes(inv._id))
        }
        catch (error) {
            console.error("[InvoicePaymentViewController] Failed to load invoices:", error)
            this._invoices = []
        }
        
        this._isShowingReceipt = NO
        this._buildConfirmationContent()
        this.view.setNeedsLayoutUpToRootView()
    }
    
    private _handleCancel() {
        if (this._hasRequestedCancellation) {
            return
        }
        this._hasRequestedCancellation = YES
        UIRoute.currentRoute
            .routeByRemovingComponentNamed(InvoicePaymentViewController.routeComponentName)
            .apply()
    }
    
    private _buildConfirmationContent() {
        const count = this._invoices.length
        this.confirmationSubtitleLabel.text = count === 1
                                              ? "Tasud järgmise arve:"
                                              : `Tasud järgmised ${count} arvet:`
        
        for (const row of this._lineRows) {
            row.removeFromSuperview()
        }
        this._lineRows = []
        
        let totalAmount = 0
        for (const invoice of this._invoices) {
            const row = new InvoiceLineRowView()
                .addedAsSubviewToView(this.view) as InvoiceLineRowView
            const formatted = RHEuroFormatter.formattedCurrency(invoice.amount, {
                currency: invoice.currency ?? "EUR",
                minimumFractionDigits: 2
            })
            row.configure(invoice.description, formatted, invoice.status === "overdue")
            this._lineRows.push(row)
            totalAmount += invoice.amount
        }
        
        this.totalAmountLabel.text = RHEuroFormatter.formattedCurrency(totalAmount, {
            currency: "EUR",
            minimumFractionDigits: 2
        })
    }
    
    private async _handleConfirmPayment() {
        if (RHCore.sharedInstance.companyAffiliationStore.activeCompanyId !== this._loadedCompanyId) {
            // The active company drifted after _invoices was resolved - refuse
            // to pay against a companyId that no longer matches the invoices
            // on screen, rather than silently re-resolving a fresh one here.
            CBDialogViewShower.alert(
                "Aktiivne ettevõte on vahepeal muutunud. Palun ava arve tasumine uuesti.",
                () => this._handleCancel()
            )
            return
        }

        this.confirmButton.enabled = NO
        this.cancelButton.enabled = NO
        this.confirmButton.titleLabel.text = "Palun oota..."

        try {
            const invoiceIds = this._invoices.map(inv => inv._id)
            const response = (await SocketClient.PayInvoices({
                invoiceIds,
                companyId: this._loadedCompanyId
            })).result

            this._showReceipt(response.paidInvoices, response.receiptHtml)
        }
        catch (error) {
            console.error("[InvoicePaymentViewController] Payment failed:", error)
            this.confirmButton.enabled = YES
            this.cancelButton.enabled = YES
            this.confirmButton.titleLabel.text = "Kinnita makse"
            CBDialogViewShower.alert("Makse ebaõnnestus. Palun proovi uuesti.")
        }
    }

    override viewDidReceiveBroadcastEvent(event: UIViewBroadcastEvent) {
        super.viewDidReceiveBroadcastEvent(event)
        if (event.name === RHCore.broadcastEventName.activeCompanyDidChange) {
            // Only the confirmation phase reads live company-scoped state
            // (_invoices, the pending PayInvoices call); the receipt phase
            // shows a completed, already-paid transaction and stays as-is.
            if (
                !this._isShowingReceipt &&
                RHCore.sharedInstance.companyAffiliationStore.activeCompanyId !== this._loadedCompanyId
            ) {
                this._handleCancel()
            }
        }
    }
    
    private _showReceipt(paidInvoices: RHInvoiceEntry[], receiptHtml: string) {
        this.confirmationTitleLabel.removeFromSuperview()
        this.confirmationSubtitleLabel.removeFromSuperview()
        this.separatorView.removeFromSuperview()
        for (const row of this._lineRows) { row.removeFromSuperview() }
        this.totalSeparatorView.removeFromSuperview()
        this.totalLabel.removeFromSuperview()
        this.totalAmountLabel.removeFromSuperview()
        this.cancelButton.removeFromSuperview()
        this.confirmButton.removeFromSuperview()
        
        this.receiptView.configure(paidInvoices, receiptHtml)
        this.receiptView.addedAsSubviewToView(this.view)
        this.receiptCloseButton.addedAsSubviewToView(this.view)
        
        this._isShowingReceipt = YES
        this.view.setNeedsLayoutUpToRootView()
    }
    
    
    override layoutViewSubviews() {
        super.layoutViewSubviews()
        
        if (this._isShowingReceipt) {
            this._layoutReceiptPhase()
        }
        else {
            this._layoutConfirmationPhase()
        }
    }
    
    private _layoutConfirmationPhase() {
        const padding = this.core.paddingLength
        const labelHeight = padding * 2
        const amountColumnWidth = padding * 8
        const bounds = this.view.contentBoundsWithInset(padding * 1.5)
        
        bounds
            .rectangleWithHeight(labelHeight * 1.5)
            .assignedAsFrameOfView(this.confirmationTitleLabel)
            
            .rectangleForNextRow(padding * 0.5, labelHeight * 0.75)
            .assignedAsFrameOfView(this.confirmationSubtitleLabel)
            
            .rectangleForNextRow(padding, 1)
            .assignedAsFrameOfView(this.separatorView)
            .ENDIF((frame: UIRectangle) => {
                let current = frame
                for (const row of this._lineRows) {
                    current = current
                        .rectangleForNextRow(0, row)
                        .assignedAsFrameOfView(row)
                }
                return current
            })
            
            .rectangleForNextRow(padding * 0.5, 1)
            .assignedAsFrameOfView(this.totalSeparatorView)
            
            .rectangleForNextRow(padding * 0.5, labelHeight)
            .distributeViewsAlongWidth(
                [this.totalLabel, this.totalAmountLabel],
                undefined,
                padding,
                [nil, amountColumnWidth]
            )
            
            .rectangleForNextRow(padding * 1.5, labelHeight * 1.5)
            .distributeViewsEquallyAlongWidth(
                [this.cancelButton, this.confirmButton],
                padding
            )
            
            .rectangleForNextRow(padding, 0)
    }
    
    private _layoutReceiptPhase() {
        const padding = this.core.paddingLength
        const labelHeight = padding * 2
        const bounds = this.view.contentBoundsWithInset(padding * 1.5)
        
        bounds
            .rectangleWithHeight(this.receiptView)
            .assignedAsFrameOfView(this.receiptView)
            
            .rectangleForNextRow(padding, labelHeight * 1.5)
            .rectangleWithWidth(padding * 8, 0.5)
            .assignedAsFrameOfView(this.receiptCloseButton)
            
            .rectangleForNextRow(padding, 0)
    }
    
}
